import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, MenuItem, MenuProps, Typography } from "@mui/material";
import HoverMenu from "material-ui-popup-state/HoverMenu";
import { bindHover, bindMenu, PopupState } from "material-ui-popup-state/hooks";
import { createContext, useContext } from "react";
import { NavLink } from "react-router-dom";

import {
  breadcrumbChevronSx,
  breadcrumbDropdownMenuItemSx,
  breadcrumbDropdownMenuItemSxInventory,
  breadcrumbDropdownMenuPaperSx,
  dropdownMenuListSx,
  navDropdownDividerSx,
  navDropdownHeaderSx,
  navDropdownHomeIconSx,
} from "./dropdownMenuStyles";

type BreadcrumbStyleVariant = "default" | "inventory";
const BreadcrumbStyleVariantContext = createContext<BreadcrumbStyleVariant>("default");

export const BreadcrumbChevronIcon = () => (
  <KeyboardArrowDownIcon sx={breadcrumbChevronSx} />
);

/**
 * Keeps hover open while the pointer moves from the title into the menu.
 * bindHover lives on this wrapper; the menu is disablePortal’d so it stays
 * in the same DOM subtree (avoids the gap-close bug with HoverMenu).
 */
export const BreadcrumbHoverRoot: React.FC<
  React.PropsWithChildren<{ popupState: PopupState }>
> = ({ popupState, children }) => (
  <Box
    {...bindHover(popupState)}
    sx={{
      display: "inline-flex",
      position: "relative",
      verticalAlign: "baseline",
    }}
  >
    {children}
  </Box>
);

type BreadcrumbDropdownMenuProps = Omit<
  MenuProps,
  "open" | "anchorEl" | "onClose" | "variant"
> & {
  popupState: PopupState;
  /** Section title with home icon — same chrome as top-nav menus. */
  headerTitle?: string;
  /**
   * Inventory breadcrumb uses tighter spacing + grey palette. Any row given a
   * `description` gets the same look without it, so a new module never needs
   * to pass this — it stays for Inventory's partner-owned call site.
   */
  variant?: BreadcrumbStyleVariant;
};

export const BreadcrumbDropdownMenu: React.FC<
  React.PropsWithChildren<BreadcrumbDropdownMenuProps>
> = ({
  popupState,
  headerTitle,
  variant = "default" as BreadcrumbStyleVariant,
  children,
  ...menuProps
}) => (
  <BreadcrumbStyleVariantContext.Provider value={variant}>
    <HoverMenu
      {...menuProps}
      {...bindMenu(popupState)}
      // Keep menu under the hover root so mouse can leave the button into the list
      disablePortal
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      marginThreshold={8}
      PaperProps={{
        sx: breadcrumbDropdownMenuPaperSx,
        elevation: 0,
      }}
      MenuListProps={{
        dense: true,
        disablePadding: true,
        sx: {
          ...dropdownMenuListSx,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        },
      }}
    >
      {headerTitle ? (
        <>
          <MenuItem disableRipple disabled sx={navDropdownHeaderSx}>
            <HomeOutlinedIcon sx={navDropdownHomeIconSx} />
            <Typography variant="inherit">{headerTitle}</Typography>
          </MenuItem>
          <Box component="li" aria-hidden sx={navDropdownDividerSx} />
        </>
      ) : null}
      {children}
    </HoverMenu>
  </BreadcrumbStyleVariantContext.Provider>
);

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85). Keyed exactly like the module's label map (`MODE_LABELS` /
 * `breadcrumbMenu`), so the two sit side by side in `modes.ts` and the label
 * map's own consumers (the nav registry's `subMenus`, `modeLabel`) keep their
 * `segment -> string` shape untouched.
 */
export type ModeDescriptions = { readonly [segment: string]: string };

/**
 * The description for one mode, or `undefined` when the module gives none.
 *
 * hasOwnProperty, NOT a bare index: `descriptions["constructor"]` on an object
 * literal is Object's constructor FUNCTION, which React would try to render as
 * a child and throw on. Same trap `isModeSegment` guards in every modes.ts.
 */
export const describeMode = (
  descriptions: ModeDescriptions,
  segment: string
): string | undefined =>
  Object.prototype.hasOwnProperty.call(descriptions, segment)
    ? descriptions[segment]
    : undefined;

interface BreadcrumbMenuLinkItemProps {
  to: string;
  label: string;
  /**
   * Grey line under the bold label — Inventory's breadcrumb, and since H76
   * (Aaron 2026-09-28: "ALL model switchers should have name and description
   * the same as demand") every module's. Omit it and the row falls back to
   * the label-only row, spacing included.
   */
  description?: string;
  selected?: boolean;
  end?: boolean;
  onClick?: () => void;
}

/** The row's look, owned once: link rows and action rows share it (BP-85). */
const useBreadcrumbItemSx = (described: boolean) => {
  const variant = useContext(BreadcrumbStyleVariantContext);
  // ONE described row, Inventory's. A row that carries a description gets
  // Inventory's tighter padding and grey palette whatever the menu's variant,
  // so every module's switcher reads exactly like Inventory / Demand (BP-85).
  // A label-only row keeps the default spacing — that is the clean fallback
  // for a mode with no description, not a second look to maintain.
  return variant === "inventory" || described
    ? breadcrumbDropdownMenuItemSxInventory
    : breadcrumbDropdownMenuItemSx;
};

const BreadcrumbItemText: React.FC<{ label: string; description?: string }> = ({
  label,
  description,
}) => (
  <>
    <Typography
      component="span"
      sx={{
        display: "block",
        fontSize: "15px",
        // "Roboto-Bold" is not a loaded family (index.html loads Roboto
        // 300/400/500/700), so weight has to carry the boldness.
        fontFamily: '"Roboto-Bold", "Roboto", sans-serif',
        fontWeight: 700,
        lineHeight: 1.3,
        color: "inherit",
      }}
    >
      {label}
    </Typography>
    {description ? (
      <Typography
        component="span"
        className="breadcrumb-menu-desc"
        sx={{
          display: "block",
          fontSize: "12px",
          fontFamily: '"Roboto-Regular", "Roboto", sans-serif',
          lineHeight: 1.3,
          color: "#898b8e !important",
          mt: 0.15,
        }}
      >
        {description}
      </Typography>
    ) : null}
  </>
);

/** Whole-row NavLink — padding is inside the clickable target (no dead zones). */
export const BreadcrumbMenuLinkItem: React.FC<BreadcrumbMenuLinkItemProps> = ({
  to,
  label,
  description,
  selected = false,
  end = false,
  onClick,
}) => {
  const itemSx = useBreadcrumbItemSx(Boolean(description));
  return (
    <MenuItem
      component={NavLink}
      to={to}
      end={end}
      onClick={onClick}
      selected={selected}
      sx={{
        ...itemSx,
        textDecoration: "none",
        color: "inherit",
      }}
    >
      <BreadcrumbItemText label={label} description={description} />
    </MenuItem>
  );
};

/**
 * A switcher row that sets a VIEW of the page rather than opening a route — the
 * same row as BreadcrumbMenuLinkItem, for a mode kept in state (Reports and Forms'
 * template layer, Default / Customer: Aaron 2026-10-21, "this mode switcher not
 * following patterns"). A mode that is a route keeps BreadcrumbMenuLinkItem.
 */
export const BreadcrumbMenuActionItem: React.FC<
  Omit<BreadcrumbMenuLinkItemProps, "to" | "end" | "onClick"> & { onClick: () => void }
> = ({ label, description, selected = false, onClick }) => {
  const itemSx = useBreadcrumbItemSx(Boolean(description));
  return (
    <MenuItem onClick={onClick} selected={selected} sx={{ ...itemSx, color: "inherit" }}>
      <BreadcrumbItemText label={label} description={description} />
    </MenuItem>
  );
};
